// @ts-strict-ignore
import { useRouter } from 'next/router';
import { useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { pollResultsShow, pollResultsHide, pollVote } from 'src/modules/community/poll/actions';
import PollAnswer from 'src/modules/community/poll/pollAnswer';
import Button from 'src/components/buttons/button';
import PollResult from 'src/modules/community/poll/pollResult';
import ActionsDropdown from 'src/modules/community/singlePost/components/actionsDropdown';
import { showSignupModal, showModal } from 'src/modules/shared/modal/actions';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Element, Section } from 'src/constants/footfall';
import { followCommunity } from 'src/modules/community/shared/actions';
import { Poll as PollType } from 'src/constants/types';
import Spacer, { Size } from 'src/components/spacer';
import { ModalType } from 'src/constants/modalTypes';
import { ModalProps } from 'src/modules/shared/modal/components/defaultModal';
import FollowSuccessMessage from 'src/modules/community/shared/components/alert/followSuccessMessage';

export default function PollAnswers(): JSX.Element | null {
  const dispatch = useDispatch();
  const router = useRouter();
  const poll = useSelector((state) => state.pages.singlePost.post as PollType);
  const { body, choices, id, isSingleAnswer, isResults, isVotingAllowed, urlEncodedTitle, userVote } = poll;
  const { currentUser, currentCommunity } = useSelector((state) => state.context);
  const [selectedChoices, setSelectedChoices] = useState<string[]>([]);

  if (!choices) return null;

  const submitVote = () => {
    if (!currentUser) {
      dispatch(showSignupModal());
      trackFootfallEvent(Event.Clicked, {
        clickType: 'sign-up',
        clickMetadata: { section: Section.CommunityPoll, element: Element.Vote },
      });
    } else if (!currentUser.followedCommunities.find(({ slug }) => slug === currentCommunity.slug)) {
      const modalProps: ModalProps = {
        acceptButtonText: i18n.t('Join'),
        cancelButtonText: i18n.t('Cancel'),
        children: i18n.t('You need to join this community to be able to vote on a poll'),
        title: i18n.t('Vote on a poll'),
        onAccept: async () => {
          await dispatch(followCommunity({ successMessage: <FollowSuccessMessage communityName={currentCommunity.name} /> }));
          dispatch(pollVote(id, currentCommunity.slug, selectedChoices));
        },
      };
      dispatch(showModal({ modalType: ModalType.Default, modalProps }));
    } else {
      dispatch(pollVote(id, currentCommunity.slug, selectedChoices));
    }
  };

  const showResults = () => {
    // update the url but don't reload page
    history.pushState(null, '', `${router.asPath}/result`);
    dispatch(pollResultsShow());
  };
  const backToVoting = () => {
    history.pushState(null, '', `/${currentCommunity.slug}/polls/${id}/${urlEncodedTitle}`);
    dispatch(pollResultsHide());
    setSelectedChoices([]);
  };

  const message = isSingleAnswer ? i18n.t('Please select one:') : i18n.t('Please select all that apply:');

  const onChange = (id: string, checked: boolean) => {
    if (checked) {
      if (isSingleAnswer) {
        setSelectedChoices([id]);
      } else {
        setSelectedChoices([...selectedChoices, id]);
      }
    } else {
      setSelectedChoices(selectedChoices.filter((c) => c !== id));
    }
  };

  return (
    <>
      {body && <ExtraInfo>{body}</ExtraInfo>}
      {isVotingAllowed && !isResults && <SelectMessage>{message}</SelectMessage>}
      {choices.map(({ id, title, votes }) => isVotingAllowed && !isResults ? (
        <PollAnswer
          isChecked={selectedChoices.includes(id)}
          key={id}
          title={title}
          type={isSingleAnswer ? 'radio' : 'checkbox'}
          onChange={(checked) => onChange(id, checked)}
        />
      ) : (
        <PollResult
          key={id}
          title={title}
          votes={votes}
        />
      ))}
      {userVote?.length > 0 &&
        <YourVote>{i18n.t('Your vote: ')}<strong>{userVote.join(', ')}</strong></YourVote>
      }
      <Spacer size={Size.L} />
      <Actions>
        {isVotingAllowed && !isResults && (
          <>
            <Button disabled={selectedChoices.length === 0} onClick={submitVote}>{i18n.t('Vote')}</Button>
            <Button type="secondary" onClick={showResults}>{i18n.t('View results')}</Button>
          </>
        )}
        {isResults && isVotingAllowed && userVote.length === 0 &&
          <Button onClick={backToVoting}>{i18n.t('Vote')}</Button>
        }
        {currentUser && (
          <ActionsDropdown
            buttonText={i18n.t('More')}
            dataTestId="poll-more-dropdown"
            post={poll}
          />
        )}
      </Actions>
    </>
  );
}

const ExtraInfo = styled.p`
  margin-bottom: 20px;
`;
const SelectMessage = styled.p`
  margin-bottom: 10px;
`;
const YourVote = styled.p`
  margin-top: 20px;
`;
const Actions = styled.div`
  button {
    margin-right: 10px;
  }
`;
